import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { AbstractControl, FormControl, FormGroup } from '@angular/forms';
import { Subject } from 'rxjs';
import { animation } from 'app/shared/utils/animation-custom';

@Component({
    selector: 'mh-select',
    templateUrl: './select.component.html',
    animations: [animation.rotate],
    standalone: false
})
export class SelectComponent<T = any> implements AfterViewInit, OnInit, OnDestroy {
  @ViewChild('inputSection') inputSection!: ElementRef;
  @ViewChild('optionsMenu') optionsMenu!: ElementRef;

  private destroy$ = new Subject<void>();
  private _options: T[] = [];
  private _placeholder = '';
  private _selectedIndex = -1;

  inputControl = new FormControl('');
  displayValue = '';

  @Input() maxHeight = 160;
  @Input() showClearButton = false;
  @Input() allowMultipleSelection = false;
  @Input() searchable = false;

  @Input() isOpen = false;

  @Input() disabled = false;
  @Input() inputId = '';

  @Input() formGroup!: FormGroup;
  @Input() keyControl!: string;
  @Input() nestedControl: string | null = null;
  @Input() nestedTypeOption = '';

  @Input() displayFn!: (option: T) => string;
  @Input() customClasses = '';
  @Input() iconClass = 'far fa-angle-down';
  @Input() selectedOption: T | null = null;

  @Input() set options(options: T[]) {
    this._options = options || [];
    this._selectedIndex = -1;
  }

  get options(): T[] {
    return this._options;
  }

  @Input() set placeholder(placeholder: string) {
    this._placeholder = placeholder;
  }

  get placeholder(): string {
    return this._placeholder;
  }

  get inputWidth(): number {
    if (this.inputSection) {
      const rect = this.inputSection.nativeElement.getBoundingClientRect();
      return rect.width > 0 ? rect.width : 500;
    }
    return 500;
  }

  @Output() optionChanged = new EventEmitter<T>();

  constructor(private cdr: ChangeDetectorRef) {}

  ngOnInit(): void {
    if (this.selectedOption) {
      this.selectOption(this.selectedOption, false);
    }
  }

  ngAfterViewInit(): void {
    this.cdr.detectChanges();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  @HostListener('keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    if (this.disabled) return;

    switch (event.key) {
      case 'Enter':
      case ' ':
        event.preventDefault();
        if (this.isOpen && this._selectedIndex >= 0) {
          this.selectOption(this.options[this._selectedIndex]);
        } else {
          this.toggleDropdown();
        }
        break;
      case 'Escape':
        event.preventDefault();
        this.closeDropdown();
        break;
      case 'ArrowDown':
        event.preventDefault();
        if (this.isOpen) {
          this.navigateOptions(1);
        } else {
          this.toggleDropdown();
        }
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (this.isOpen) {
          this.navigateOptions(-1);
        } else {
          this.toggleDropdown();
        }
        break;
    }
  }

  toggleDropdown(): void {
    if (!this.disabled) {
      this.isOpen = !this.isOpen;
      if (this.isOpen) {
        this._selectedIndex = this.findSelectedIndex();
        this.cdr.detectChanges();
      }
    }
  }

  closeDropdown(): void {
    this.isOpen = false;
    this._selectedIndex = -1;
  }

  selectOption(option: T, emit = true): void {
    const control = this.getFormControl();
    const value = this.getOptionValue(option);
    
    control?.setValue(value);
    this.updateDisplayValue(option);
    this.closeDropdown();
    
    if (emit) {
      this.optionChanged.emit(option);
    }
  }

  onBackdropClick(): void {
    this.closeDropdown();
  }

  onOverlayDetach(): void {
    this.closeDropdown();
  }

  clearSelection(): void {
    if (this.showClearButton && !this.disabled) {
      const control = this.getFormControl();
      control?.setValue(null);
      this.displayValue = '';
      this._placeholder = this.placeholder;
      this.optionChanged.emit(null as any);
    }
  }

  isOptionSelected(option: T): boolean {
    return this.selectedOption === option;
  }

  isOptionHighlighted(index: number): boolean {
    return this._selectedIndex === index;
  }

  getOptionClasses(option: T, index: number): string {
    const baseClasses = 'tw-py-2 tw-px-3 tw-text-main-text tw-cursor-pointer hover:tw-text-main-orange hover:tw-bg-gray-50 tw-transition-all select-option';
    const selectedClass = this.isOptionSelected(option) ? ' tw-bg-blue-50 tw-text-blue-600' : '';
    const highlightedClass = this.isOptionHighlighted(index) ? ' tw-bg-gray-100' : '';
    
    return `${baseClasses}${selectedClass}${highlightedClass}`;
  }

  private getFormControl(): AbstractControl | null {
    if (!this.formGroup || !this.keyControl) return null;
    
    return this.nestedControl 
      ? this.formGroup.get(this.nestedControl)?.get(this.keyControl)
      : this.formGroup.get(this.keyControl);
  }

  private getOptionValue(option: T): any {
    return this.nestedTypeOption 
      ? (option as any)[this.nestedTypeOption]
      : option;
  }

  private updateDisplayValue(option: T): void {
    this.displayValue = this.displayFn(option);
    this._placeholder = this.displayFn(option);
  }

  private navigateOptions(direction: number): void {
    if (this.options.length === 0) return;

    const newIndex = this._selectedIndex + direction;
    
    if (newIndex >= 0 && newIndex < this.options.length) {
      this._selectedIndex = newIndex;
    } else if (direction > 0) {
      this._selectedIndex = 0;
    } else {
      this._selectedIndex = this.options.length - 1;
    }
  }

  private findSelectedIndex(): number {
    if (!this.selectedOption) return -1;
    return this.options.findIndex(option => option === this.selectedOption);
  }

  trackByFn(index: number, option: T): any {
    return option;
  }
}
